import type { RadioPlayer } from '../../audio/radio';
import { RadioControls } from '../RadioControls';
import type { Settings } from '../settings';
import { GamepadMonitor } from './GamepadMonitor';
import { LookSettings } from './LookSettings';

interface Props {
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
  onReset: () => void;
  /** Take the guided first game's lessons again, from the first. */
  onResetGuide: () => void;
  onBack: () => void;
  /**
   * Shown over a game rather than from the title screen: only the settings that change the game
   * being played, and a Close rather than a Back, because there is no page to go back to.
   */
  inGame?: boolean;
  radio: RadioPlayer;
}

/** Preferences that outlive a game; stored in this browser. */
export function SettingsPanel({
  settings,
  onChange,
  onReset,
  onResetGuide,
  onBack,
  inGame = false,
  radio,
}: Props) {
  const { sound } = settings;
  return (
    <>
      <h2 className="menu-heading">Settings</h2>

      <div className="menu-row">
        <span className="menu-label">Smoke</span>
        <div className="choice" role="radiogroup" aria-label="Smoke over the board">
          {[false, true].map((on) => (
            <button
              key={String(on)}
              role="radio"
              aria-checked={settings.fog === on}
              className={settings.fog === on ? 'active' : ''}
              onClick={() => onChange({ fog: on })}
              data-testid={on ? 'setting-fog-on' : 'setting-fog-off'}
            >
              {on ? 'On' : 'Off'}
            </button>
          ))}
        </div>
        <label className="slider">
          Thickness
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.05}
            value={settings.fogDensity}
            disabled={!settings.fog}
            aria-label="Smoke thickness"
            onChange={(e) => onChange({ fogDensity: Number(e.target.value) })}
          />
          <span className="slider-value">{percent(settings.fogDensity)}</span>
        </label>
      </div>
      <p className="hint">Heavy smoke drifting over the board; the pieces stand out of it.</p>

      <div className="menu-row">
        <span className="menu-label">Threat hints</span>
        <div className="choice" role="radiogroup" aria-label="Threat hints on the board">
          {[false, true].map((on) => (
            <button
              key={String(on)}
              role="radio"
              aria-checked={settings.threatHints === on}
              className={settings.threatHints === on ? 'active' : ''}
              onClick={() => onChange({ threatHints: on })}
              data-testid={on ? 'setting-threat-hints-on' : 'setting-threat-hints-off'}
            >
              {on ? 'On' : 'Off'}
            </button>
          ))}
        </div>
      </div>
      <p className="hint">
        Win odds on the squares a piece can attack, and a warning ring under pieces the enemy could
        take next move.
      </p>

      <div className="menu-row">
        <span className="menu-label">Guided game</span>
        <div className="choice" role="radiogroup" aria-label="Lessons during the first game">
          {[false, true].map((on) => (
            <button
              key={String(on)}
              role="radio"
              aria-checked={settings.guide === on}
              className={settings.guide === on ? 'active' : ''}
              onClick={() => onChange({ guide: on })}
              data-testid={on ? 'setting-guide-on' : 'setting-guide-off'}
            >
              {on ? 'On' : 'Off'}
            </button>
          ))}
        </div>
        <button onClick={onResetGuide} data-testid="setting-guide-again">
          Show again
        </button>
      </div>
      <p className="hint">
        Short lessons on the sign beyond the table and on the screen, each the first time the board
        calls for it: moves, attacks, the cycle of light and dark, power points and spells.
      </p>

      <RadioControls
        radio={radio}
        compact
        sound={sound}
        onSoundChange={(next) => onChange({ sound: next })}
      />
      <p className="hint">
        Every sound is synthesised on the spot, and each character has a voice of its own.
      </p>

      <LookSettings settings={settings} onChange={onChange} />

      <div className="menu-row">
        <span className="menu-label">Controller</span>
      </div>
      <GamepadMonitor />
      <p className="hint">
        On the board: left stick or D-pad moves the cell ring, the bottom face button picks, the
        right one lets go; the right stick turns the table, and pressed in it zooms up and down and
        pans left and right; RB zooms in and LB out. Back moves the pad to the tablets, a message
        and this sound panel, where the bumpers turn the focused slider. In the arena: the right
        trigger and the bottom and left face buttons fire, the left trigger and the right and top
        face buttons dash, the sticks move. An 8BitDo pad wants its X (Xbox) mode for this layout.
      </p>

      <div className="menu-actions">
        <button onClick={onReset}>Reset</button>
        <button onClick={onBack} data-testid="settings-done">
          {inGame ? 'Close' : 'Back'}
        </button>
      </div>
    </>
  );
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}
